import { useEffect, useState } from "react";
import TodoApp from "../../TodoApp";
import { useSubscription } from "../../hooks/useSubscription";
import FirstChargeAlert from "./FirstChargeAlert";
import Paywall from "./Paywall";

function checkoutJustSucceeded(): boolean {
  return new URLSearchParams(window.location.search).get("checkout") === "success";
}

function PlanSpinner({ label }: { label: string }) {
  return (
    <div className="min-h-screen bg-warm-bg-base flex items-center justify-center">
      <div
        role="status"
        aria-label={label}
        className="w-8 h-8 border-[3px] border-warm-border border-t-warm-blue rounded-full animate-spin"
      />
    </div>
  );
}

function PlanNotice({
  message,
  action,
  onAction,
  alert = false,
}: {
  message: string;
  action: string;
  onAction: () => void;
  alert?: boolean;
}) {
  return (
    <div className="min-h-screen bg-warm-bg-base flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        <p className="text-warm-text-primary mb-4" role={alert ? "alert" : undefined}>
          {message}
        </p>
        <button
          type="button"
          onClick={onAction}
          className="px-4 py-2 rounded-soft bg-warm-blue hover:bg-warm-blue-dark text-white font-medium transition-colors"
        >
          {action}
        </button>
      </div>
    </div>
  );
}

/**
 * The website gate. A trial or a paid month opens the app.
 * Owner accounts in billing_exemptions skip this screen.
 */
export default function SubscribedApp() {
  const {
    hasAccess,
    hasUsedTrial,
    paymentFailed,
    paymentFix,
    subscriptionLoadFailed,
    isLoading,
    showFirstChargeAlert,
    subscription,
    createCheckoutSession,
    createPortalSession,
    refetch,
  } = useSubscription();
  const [waitingForCheckout, setWaitingForCheckout] = useState(checkoutJustSucceeded);

  useEffect(() => {
    if (!waitingForCheckout || hasAccess) return;
    void refetch();
    let attempts = 0;
    const poll = setInterval(() => {
      attempts += 1;
      void refetch();
      if (attempts >= 15) {
        clearInterval(poll);
        setWaitingForCheckout(false);
      }
    }, 2000);
    return () => clearInterval(poll);
  }, [waitingForCheckout, hasAccess, refetch]);

  if (isLoading || (waitingForCheckout && !hasAccess)) {
    return (
      <PlanSpinner
        label={waitingForCheckout ? "Opening your plan" : "Loading your plan"}
      />
    );
  }

  if (checkoutJustSucceeded() && !hasAccess) {
    return (
      <PlanNotice
        message="Your payment is still opening. This page will not start a second payment."
        action="Check again"
        onAction={() => setWaitingForCheckout(true)}
      />
    );
  }

  if (subscriptionLoadFailed && !hasAccess) {
    return (
      <PlanNotice
        message="Your plan could not be loaded."
        action="Try again"
        onAction={() => void refetch()}
        alert
      />
    );
  }

  if (!hasAccess) {
    return (
      <Paywall
        hasUsedTrial={hasUsedTrial}
        paymentFailed={paymentFailed}
        paymentFix={paymentFix}
        onCheckout={createCheckoutSession}
        onFixPayment={createPortalSession}
      />
    );
  }

  const quit = async () => {
    const url = await createPortalSession();
    if (url) window.location.href = url;
  };

  const alert =
    showFirstChargeAlert && subscription?.current_period_end ? (
      <FirstChargeAlert
        chargeOn={subscription.current_period_end}
        onQuit={quit}
      />
    ) : null;

  if (!alert) return <TodoApp />;

  return (
    <div className="h-dvh flex flex-col bg-warm-bg-base">
      {alert}
      <div className="min-h-0 flex-1 overflow-auto">
        <TodoApp />
      </div>
    </div>
  );
}
